/** @module IMSDK */

/**
 * c-imsdk UI层与Instant底层网络通信库的桥梁
 * @author gabewu
 */
import mitt from 'mitt';

// @ts-ignore
import createProto from './proto.js';

import { _log, _error } from './utils/log';

function Mango(params: Object) {
  // 私有化中移除这里的上报
  // let url = '//wss.qidian.qq.com/speed';
  // if (location.hostname === 'oaadmin.qidian.qq.com') {
  //   url = '//testwss.qidian.qq.com/speed';
  // }

  // const query = '?data=' + encodeURIComponent(JSON.stringify(params));
  // let img = new Image();
  // img.src = url + query;
}

interface ReportFunc {
  (params?: object): void;
}

export interface MsgTag {
  tag: String;
  value: String;
}

/**
 * 客户资料
 */
export interface CustomerInfo {
  /** 姓名 */
  name?: string;
  /** 头像图片的url */
  face?: string;
  /** 签名 */
  sign?: string;
  /** 性别，1---男，2---女 */
  gender?: number;
  /** 手机号 */
  mobile?: string;
  /** 邮箱 */
  email?: string;
  // /** 国家 */
  // country?: string;
  // /** 省 */
  // province?: string;
  // /** 市 */
  // city?: string;
  // /** 区县 */
  // area?: string;
}

/**
 * SDK实例的interface
 */
export interface SDK {
  /** @internal */
  _sid: string;
  _buildTime: string;
  /** 配置 */
  config: SDKConfig;
  /** @internal 会话是否已经进线成功 */
  sessionStarted: boolean;
  /** 创建IM会话 */
  create?(options: object): void;
  /** 登录 */
  login?(params: object, wpaDetail?: any): void;
  /** 登出 */
  logout?(): void;
  /** 断开重连 */
  reConnect?(): void;
  /** 创建文本消息 */
  createTextMessage?(params: MessageText): MessagePackage;
  /** 创建图片消息 */
  createImageMessage?(params: MessageImage): MessagePackage;
  /** 创建图片上传消息 */
  createImageUploadMessage?(params: object): object;
  /** 创建图片下载消息 */
  createImageDownloadMessage?(params: object): object;
  /** 创建图文消息 */
  createImageTextMessage?(params: object): MessagePackage;
  /** 创建语音消息 */
  createVoiceMessage?(params: MessageVoice): MessagePackage;
  /** 创建文件消息 */
  createFileMessage?(params: MessageFile): MessagePackage;
  /** 创建文件上传消息 */
  createFileUploadMessage?(params: object, objType?: OBJTYPES): object;
  /** 创建文件下载消息 */
  createFileDownloadMessage?(params: object, objType?: OBJTYPES): object;
  /** 创建企点XML结构化消息 */
  createQDXMLMessage?(params: MessageQDXML, objType?: OBJTYPES): MessagePackage;
  /** 创建长文本消息 */
  createLongTextMessage?(params: MessageLongText): MessagePackage;
  /** 
   * 发送消息
   * @param message 消息数据包
   * @return 消息发送结果
   * @example
   * ```ts
   * const message = imsdk.createTextMessage({...});
   * const promise = imsdk.sendMessage(message);
   * promise.then(res => {
   *  if(res.code) {
   *    // 发送失败
   *  } else {
   *    // 发送成功处理
   *  }
   * }).catch(() => {
   *  // 异常处理
   * });
   * ```
   */
  sendMessage?(message: MessagePackage): Promise<object>;
  /** 重发消息 */
  resendMessage?(seq: number): Promise<object>;
  /** @internal 撤回消息 */
  revokeMessage?(message: object): Promise<object>;
  /** 获取文件、图片、语音等下载地址 */
  sendUploadOrDownloadMessage?(message: object): Promise<object>;
  /** @internal 新消息推送后同步消息发送 */
  sendSyncMessage?(seq: number, syncMessage: Array<object>): void;
  /** @internal 全体未读消息请求 */
  sendContactMessage?(context?: string, seq?: number): void;
  /** @internal 同步消息推送 */
  onMessageNotify?(resutl: object): void;
  /** @internal 生成消息摘要 */
  createMessageSummary?(message: object): string;
  /** 获取漫游消息列表 */
  getMessageList?(params: MessageListReq): Promise<object> | void;
  /** @internal 已读上报 */
  setMessageRead?(params: object): Promise<object> | void;
  /** @internal 更新未读消息 */
  updateUnReadMessage?(): void;
  /** 事件绑定 */
  on?(type: EVENT, handler: Function): void;
  /** 事件解绑 */
  off?(type: EVENT, handler: Function): void;
  /** 触发事件 */
  emit?(type: EVENT, event?: object): void;
  /** @internal 设置日志上报接口 */
  setLogReport?(report: ReportFunc): void;
  // 私有化进线
  /** @internal 用户进线 */
  clientIncoming?(params: any): void;
  /** 发送内部点击事件消息 */
  sendInnerClickEvent?(params: MessageInnerEvent): void;
  /** 结束当前会话。重新发送消息后重新开启会话。 */
  closeSession?(params: any): void;
  /** 获取接待员工信息 */
  fetchExtuinInfo?(params: any, isRobot?: boolean): void;
  /** @internal 内部命令发送 */
  sendCmdMessage?(command: number, params: any, msgtype?: number): any;
  /** 获取消息中附加的tags */
  getMessageTags?(msg: any): Array<MsgTag>;
  /** 设置消息中的附加的tags */
  setMessageTags?(msg: any, tags: Array<MsgTag>): void;
  /** 同步客户信息到企点 */
  setCustomerInfo?(cInfo: CustomerInfo): void;
  // Saas版进线
  /** 会话进线 */
  c2bSessionStart?(params: SessionConfig): void;
  /** @internal C侧主动满意度评价 */
  activeFeedback?(params: any): void;
  /** @internal 被压缩长文本的下载 */
  createCompressedLongTextDownloadMessage?(params: object, type?: string): object;
  /** @iternal 获取退出排队的消息，后续调用http接口发送 */
  createExitQueueMessage?(params: object): object;
  /** @iternal 获取老webim漫游消息列表 */
  getOldMessageList?(params: MessageListReq): Promise<object> | void;
  /** 获取当前实例绑定的socketio */
  getSocket?(): any;
  /** 获取接待状态 */
  getReceptionStatus?(): any;
  /** @internal 更新接待信息 */
  updateReception?(params: any): void;
  /** 创建【设置消息已被阅读】的消息包 */
  createReportMsgReadReportReq?(params: any): void;
  /** 创建【拉取消息已读状态】的消息包 */
  createGetMsgReadInfoReq?(params: any): void;
  /** 推送消息已读回执状态 */
  pushMsgReadReport?(params: any): Promise<object>;
  /** 读取消息的【crm】信息 */
  getMessageCrmData?(message: MessagePackage): object;
  /** 设置消息的【crm】信息 */
  setMessageCrmData?(message: MessagePackage, crmData: object): MessagePackage;
  /** 获得socket连接的配置信息，用于为SDKUtils发送http请求设置headers */
  getConnectConfig?(): any;
}

/**
 * login方法登录参数的interface
 */
export interface LoginOptions {
  /** 分配给用户的id */
  uin: Number;
  /** 接待方主号/客服号 */
  corpUin: Number;

  term?: Number;
}

/**
 * SDK的配置项
 */
export interface SDKConfig {
  /** 拉取漫游消息时每次拉取的数据，默认30 */
  roamingMsgSize?: number;
  /** 同步消息时每次的数量，默认30 */
  syncMsgSize?: number;
  /** @internal 每次拉取联系人的数量，默认50 */
  contactMsgSize?: number;
  /** @internal 渠道类型。Saas版为50， 私有化版为 4。默认为50 */
  channelType?: number;
  /** @internal cos文件存储的桶id。默认值0，对外第三方sdk时需要传入值1。 */
  cosBucket?: number;
}

/**
 * SDK事件列表
 */
export enum EVENT {
  /** IM准备就绪。 回调函数无入参 */
  SDK_READY = 'SDK_READY',
  /** 收到新消息推送。回调函数入参：{@link SyncMessageListObject} */
  MESSAGE_RECEIVED = 'MESSAGE_RECEIVED',
  /** @internal 更新未读消息 */
  MESSAGE_UNREAD_UPDATED = 'MESSAGE_UNREAD_UPDATED',
  /** 收到通知。回调函数入参：{@link NotifyListObject} */
  NOTIFY_PUSH = 'NOTIFY_PUSH',
  /** 被踢下线。 回调函数无入参 */
  KICKED_OUT = 'KICKED_OUT',
  /** 本地提示。主要用来做一些前端自身产生的消息提醒。 */
  LOCAL_NOTIFY = 'LOCAL_NOTIFY',
  /** 输入中... */
  TYPING = 'TYPING',
  /** @internal 音视频通话事件 */
  NOTIFY_RTC = 'NOTIFY_RTC',
  /**
   * 接待变化事件。回调函数入参格式： {@link ReceptionParams}
   */
  RECEPTION_CHANGE = 'RECEPTION_CHANGE',

  /**
   * 会话开始。此时可以拉取历史消息和发送消息了。
   */
  SESSION_START = 'SESSION_START',
  /* qq 会话邀请 */
  INVITE_QQ = 'INVITE_QQ',
  /* 发送 qq 名片 */
  ADD_QQ_CARD = 'ADD_QQ_CARD',

  /** ws错误 */
  ERROR = 'error',

  READ_RECEIPT = 'READ_RECEIPT',
}

/**
 * SDK消息类型常量
 */
export enum TYPES {
  /** 文本 */
  MSG_TEXT = 'text',
  /** @internal 大表情 */
  MSG_FACE = 'face',
  /** @internal 媒体 */
  MSG_MEDIA = 'media_resources',
  /** 图片 */
  MSG_IMAGE = 'image',
  /** 长文本 */
  MSG_LONG_MSG = 'long_msg',
  /** 图文 */
  MSG_IMAGE_TEXT = 'image_text',
  /** 企点XML结构化消息 */
  MSG_QDXML = 'qdxml_msg',
  /** 文件 */
  MSG_FILE = 'file_info',
  /** 语音 */
  MSG_VOICE = 'ptt_info',
  /** 系统灰条消息 */
  MSG_GRAY = 'gray_msg',
  /** @internal 混合消息 */
  MSG_MIX = 'mix',
  /** 消息撤回 */
  MSG_REVOKE = 'revoke_msg',
  /** 机器人消息 */
  MSG_ROBOT = 'robot',
  /** @internal 机器人留资表单 */
  MSG_ROBOT_FORM = 'robot_form',
  /** 机器人导航消息 */
  MSG_ROBOT_NAV = 'robot_nav',
  /** @internal 满意度 */
  MSG_FEED_BACK = 'feed_back',
  /** SDK-机器人消息，下发的想xml数据，前端需要xml2json后，自行模板渲染 */
  MSG_ROBOT_SDK = 'robot_sdk',
  /** 通用三方消息。自行根据crmFlag值进行判断解析 */
  MSG_THIRD_MSG = 'third_msg',
  /** 音视频通话消息 */
  MSG_RTC = 'rtc_msg',
}

/**
 * SDK对象类型常量(用于cos平台上传下载)
 */
export enum OBJTYPES {
  /** 未知 */
  OBJ_UNKNOWN = 1,
  /** 图片 */
  OBJ_IMAGE = 2,
  /** 语音 */
  OBJ_VOICE = 3,
  /** 文件 */
  OBJ_FILE = 4,
  /** 长文本 */
  OBJ_LONGTEXT = 5,
  /** 图文 */
  OBJ_IMGTXT = 6,
}

// Saas 会话进线配置
/**
 * 会话进线的配置。
 */
export interface SessionConfig {
  /** 分配类型。 1-单员工接待；2-分组接待；3-机器人接待 */
  assignType: number;
  /** 分配ID：根据分配类型，填员工号/分组ID/机器人ID */
  assignId: number;
  /** 将要进线的cid。由 appid#aid 拼接 */
  cid: string;
}

/**
 * 消息定义
 */
interface MessagePackage {
  msg_send_req: any;
}

/**
 * 文字消息
 */
export interface MessageText {
  /** 接收方 */
  to: number;
  /** 消息载荷 */
  payload: {
    /** 发送方 */
    user_id: string | number;
    user_type?: number;
    pubacc_id?: string | number;
    /** 文本内容 */
    text: Array<{
      /** 类型，应填写 'text' */
      type: string;
      /** 文本内容 */
      content: string;
    }>;
  };
}

/**
 * 图片消息
 */
export interface MessageImage {
  /** 接收方 */
  to: number;
  /** 消息载荷 */
  payload: {
    /** 发送方 */
    user_id: string | number;
    user_type?: number;
    pubacc_id?: string | number;
    /** 图片信息 */
    image: {
      /** uuid */
      uuid: string;
      /** 图片path，如果是调用的imsdk上传的，则此值同uuid */
      path: string;
      /** 图片名称 */
      name: string;
      /** 图片大小 */
      size: number;
      /** 宽 */
      width: number;
      /** 高 */
      height: number;
      format?: string;
    };
  };
}

/**
 * 文件消息
 */
export interface MessageFile {
  /** 接收方 */
  to: number;
  /** 消息载荷 */
  payload: {
    /** 发送方 */
    user_id: string | number;
    user_type?: number;
    pubacc_id?: string | number;
    /** 文件信息 */
    file: {
      /** uuid */
      uuid: string;
      /** 文件path，如果是调用的imsdk上传的，则此值同uuid */
      path: string;
      /** 文件名称 */
      name: string;
      /** 文件大小 */
      size: number;
      /** 文件类型 */
      format: string;
    };
  };
}

/**
 * 语音消息
 */
export interface MessageVoice {
  /** 接收方 */
  to: number;
  /** 消息载荷 */
  payload: {
    /** 发送方 */
    user_id: string | number;
    user_type?: number;
    pubacc_id?: string | number;
    /** 语音信息 */
    ptt: {
      /** uuid */
      uuid: string;
      /** 语音path，如果是调用的imsdk上传的，则此值同uuid */
      path: string;
      /** 语音名称 */
      name: string;
      /** 语音大小 */
      size: number;
      /** 语音类型 */
      format: string | number;
      /** 语音时长，单位秒 */
      length: number;
    };
  };
}

/**
 * 企点规范XML消息
 */
export interface MessageQDXML {
  /** 接收方 */
  to: number;
  /** 消息载荷 */
  payload: {
    /** 发送方 */
    user_id: string | number;
    user_type?: number;
    pubacc_id?: string | number;
    /** xml信息 */
    qdXml: {
      /** 主体xml信息 */
      msg_common_control_msg_info:
        | string
        | {
            /** xml文本经uploadLongText接口上传后获得的uuid */
            str_control_msg_resid: string;
            /** 简介 */
            str_msg_brief: string;
            /** xml原始文本 */
            bytes_structure_content: string;
          };
      /** 额外信息 */
      msg_third_party_msg_info:
        | string
        | {
            /** xml文本经uploadLongText接口上传后获得的uuid */
            string_res_id: string;
            /** 简介 */
            string_msg_brief: string;
            /** xml原始文本 */
            string_msg_content: string;
          };
    };
  };
}

/**
 * 长文本消息。当文本过长时，应使用此方法发送，以防止通道阻塞
 */
export interface MessageLongText {
  /** 接收方 */
  to: number;
  /** 消息载荷 */
  payload: {
    /** 发送方 */
    user_id: string | number;
    user_type?: number;
    pubacc_id?: string | number;
    /** 文件信息 */
    long_msg: {
      /** uuid */
      uuid: string;
      /** 文件path，如果是调用的imsdk上传的，则此值同uuid */
      path: string;
      /** 文本长度 */
      length: number;
    };
  };
}

/**
 * 企点内部事件消息
 */
export interface MessageInnerEvent {
  /** 含有特定scheme的url，如 mqqwpa://xxxx */
  args: string;
  /** 附加的文本内容 */
  text: string;
}

/**
 * 历史消息列表请求参数
 */
export interface MessageListReq {
  /** 接收方 */
  to: number;
  /** 消息起始seq */
  seq: number;
  /** 消息载荷 */
  payload: {
    /** 发送方 */
    user_id: string | number;
    user_type?: number;
    pubacc_id?: string | number;
  };
}

/** 接待变化事件回调函数的参数类型 */
export interface ReceptionParams {
  /** 是否在未接入池/排队 */
  inWaitPool?: boolean;
  /** 是否机器人 */
  isRobot?: boolean;
  /** 如果是机器人，机器人的分组id */
  robotId?: string | number;
  /** 人工接待时的工号或分组号 */
  extuin?: string | number;
}

/**
 * SDK同步推送的消息列表或拉取到的历史消息列表
 */
export interface MessageListItem {
  /** 消息序号 */
  seq: string | number;
  /** 消息类型 */
  type: TYPES;
  /** 时间，单位ms */
  time: number;
  /** 原始消息head中的特殊数据 */
  crm?: object;
  /** 原始消息的head数据 */
  msgHead?: object;
  /** 原始消息的body数据 */
  msgBody?: object;
}

/**
 * 同步推送的消息列表对象
 */
export interface SyncMessageListObject {
  /** 消息列表，主号/客服号是列表的key */
  [kfuin: string]: Array<MessageListItem>;
}

/** 历史消息列表对象 */
export interface HistoryMessageListObject {
  /** 下次拉取历史消息的起使seq */
  nextReqMessageSeq: string | number;
  /** 是否已拉取所有历史消息 */
  isCompleted: boolean;
  /** 消息列表 */
  messageList: Array<MessageListItem>;
}

/**
 * push通知的结构
 */
export interface NotifyListObject {
  /** 通知列表，主号/客服号是列表的key */
  [kfuin: string]: Array<{
    /** 通知内容 */
    content: string;
  }>;
}

function createSDKInstance() {
  /**
   * 以下变量都是sdk内部使用
   */
  let Instant;
  const emitter: any = mitt();
  const _contact_info_map = new Map(); // 联系人信息映射表
  let _user_id: number = -1; // 登录用户标识
  let _corp_uin: number = -1; // 登录用户主号
  let _socketio: any;
  let _sync_messsage_seq: number = 0; // 新消息seq存储
  let _contact_message_array: any[] = []; // 所有C侧消息信息表
  let _sdk_is_ready: boolean = false;
  let _send_sync_message_timeoutid: number = 0; // 同步消息请求定时器
  let _report: ReportFunc = function () { console.log('★report=========>', arguments[0]) }; // 上报日志函数
  let _reception: any = {};

  const _syncseq_hole_map = new Map(); // 同步推送消息空洞映射表

  /**
   * 以下用于解决以下问题
   * 问题：由于svr或者网络某种原因无法收到推送消息
   * 方案: 这种情况下只能依靠同步定时器主动拉取消息，所以对同步定时器的调用次数进行“指数退避”算法
   * 1、如果同步定时器调用一次，_send_sync_timeout_count加1
   * 2、如果收到同步推送消息，_send_sync_timeout_count减1
   *    如果主动拉到的数据为0，_send_sync_timeout_count减1
   * 3、下个同步定时器的时长根据_send_sync_timeout_count进行指数取值
   *    _send_sync_timeout_count如果大于等于最大同步定时器计数，时长会在3000ms~10000ms进行随机散布
   */
  const _max_send_sync_timeout = 3 * 60 * 1000; // 同步消息请求定时器间隔最大3分钟;
  const _min_send_sync_timeout_count = 0; // 最小同步定时器计数
  const _max_send_sync_timeout_count = 5; // 最大同步定时器计数
  let _send_sync_timeout_count = 1; // 同步定时器计数

  /**
   * 以下用于解决以下问题
   * 问题: 在消息繁忙的场景下，同步消息请求包很可能还没回包，svr也会有推送消息过来，此时如果继续发同步消息请求是没必要的
   * 方案: 在同步消息没有回包的情况下，将收到的svr最大推送id缓存起来，回包后再请求下一次同步消息
   */
  let _send_sync_loading = false; // 正在发送同步消息请求包
  let _next_sync_notify_seq = 0; // 下一个推送id

  // 创建一个 proto 实例
  const proto = createProto();

  /**
   * sdk定义
   */
  let sdk: SDK = {
    _sid: Math.random().toString(36).substring(2),
    _buildTime: process.env.BUILD_TIME,
    sessionStarted: false,
    config: {
      roamingMsgSize: 30,
      syncMsgSize: 30,
      contactMsgSize: 50,
      channelType: 50,
      cosBucket: 0, // 默认0。 对外sdk模式要传入 1
    },
  };

  /**
   * @internal
   * 初始化配置
   * @param {Object} options
   * @param {Object} options.socketio - 底层网络通信库
   */
  sdk.create = function (options: any) {
    if (_sdk_is_ready) {
      return;
    }

    _log('初始化配置值:', options);

    if (!options.socketio) {
      _error('Instant没有正确初始化', options.socketio);
      _report({
        module: 'c-imsdk',
        method: '[create][error]',
        from: _user_id,
        message: 'socketio-miss',
      });
      return;
    }

    // Instant对象，可以从window上获取（兼容web cdn模式），也可以从外部传入（兼容小程序引入）。
    if (options.Instant) {
      Instant = options.Instant;
    } else if (typeof (window) !== 'undefined' && window.Instant) {
      Instant = window.Instant
    } else {
      _error('Instant对象没有传入也为在window上找到', options.Instant);
      _report({
        module: 'c-imsdk',
        method: '[create][error]',
        from: -1,
        message: 'Instant-miss',
      });
      return;
    }
    
    _socketio = options.socketio;
  };

  /**
   * 登录
   * @param params 登录信息
   * @param wpaDetail 接待配置信息。仅私有化版本需要传入
   * @returns
   */
  sdk.login = function (params: LoginOptions, wpaDetail: any = {}) {
    // 协议初始配置
    const from = Number(params.uin);
    const corpUin = Number(params.corpUin);
    if (isNaN(from)) {
      _error('登录用户标识(uin)不是数值类型', params.uin);
      _report({
        module: 'c-imsdk',
        method: '[login][error]',
        from: from,
        message: params,
      });
      return;
    }

    _user_id = from;
    _corp_uin = corpUin;
    proto.initial({
      kfuin: corpUin,
      from: from,
      term: params.term || 5, // web工作台: 5, 移动端web工作台:7
      wpaId: String(wpaDetail.wpaId) || '', // wpa接待的id
      channelType: this.config.channelType,
      cosBucket: this.config.cosBucket, // cos文件存储的桶id
    });

    _socketio.ready().then((info: any) => {
      _log('Instant Ready', info);
      _report({
        module: 'c-imsdk',
        method: 'onReady',
        from: _user_id,
        message: `Event`,
      });
      // 通知上层:SDK已经准备好
      this.sendContactMessage!();
    });
    _socketio.on(
      Instant.NOTIFY_MESSAGE_TYPE.PUSH_NEW_MESSAGE_NOTICE,
      this.onMessageNotify
    );
    _socketio.on(Instant.NOTIFY_MESSAGE_TYPE.NOTIFY_KICK_USER, () => {
      _report({
        module: 'c-imsdk',
        method: 'onKickout',
        from: _user_id,
        message: `Event`,
      });
      clearTimeout(_send_sync_message_timeoutid);
      // 被踢下线后，清理 reception 
      emitter.emit(EVENT.RECEPTION_CHANGE, {});
      emitter.emit(EVENT.KICKED_OUT);
    });
    // 重连之后拉取消息
    _socketio.on(Instant.NOTIFY_MESSAGE_TYPE.RECONNECT, () => {
      this.sendSyncMessage!(_sync_messsage_seq + 1, []);
      // 重连成功之后，请求一下接待状态。
      this.getReceptionStatus!();
    });
    _socketio.on(
      Instant.NOTIFY_MESSAGE_TYPE.NOTIFY_NEW_SC_MESSAGE,
      (result: any) => {
        _report({
          module: 'c-imsdk',
          method: 'onScMessage',
          from: _user_id,
          message: {
            result,
          },
        });
        const sc_message_array = result;
        for (const sc of sc_message_array) {
          const subMsgType = sc['uint32_sub_msgtype'];
          const subCommand = sc['uint32_sub_command'];
          const scData = JSON.parse(sc['bytes_sc_data']);
          if (subMsgType === 0x210 && subCommand === 0x92) {
            // 新业务
            const head = scData['msg_crm_common_head'] || {};
            if (head['uint32_crm_sub_cmd'] === 122) {
              // 企业级屏蔽推送
              const notify = scData['msg_s2c_push_black_status_notify'] || {};
              // const str_i18n_key = notify['str_i18n_key'];
              const str_tips = notify['str_tips'] || '';
              const user_id = notify['bytes_source_value'];
              let user_type = notify['uint32_source_type'];
              if (str_tips && user_id && user_type) {
                if (user_type === 3) {
                  /**
                   * svr对于webim的定义是3
                   * 前端对于webim的定义是4
                   */
                  user_type = this.config.channelType;
                } else if (user_type === 1) {
                  /**
                   * svr对于微信公众号的定义是1
                   * 前端对于微信公众号的定义是2
                   */
                  user_type = 2;
                } else if (user_type === 11) {
                  /**
                   * svr对于微信小程序的定义是11
                   * 前端对于微信小程序的定义是6
                   */
                  user_type = 6;
                }
                // 找出对应的假号
                _contact_info_map.forEach((info, key) => {
                  if (
                    info.extend.str_user_id === user_id &&
                    info.extend.user_type === user_type
                  ) {
                    let data: any = {};
                    data[key] = [
                      {
                        id: key,
                        content: str_tips,
                      },
                    ];
                    emitter.emit(EVENT.NOTIFY_PUSH, data);
                  }
                });
              }
            } else if (scData['uint32_sub_cmd'] === 124) {
              //SUBCMD_RTC_OPERATE_NOTIFY = 124; //音视频操作命令推送
              let data = scData.msg_s2c_push_rtc_command_notify;
              emitter.emit(EVENT.NOTIFY_RTC, data);
            } else if (scData['uint32_sub_cmd'] === 143) {
              // 消息已读回执事件
              const data = scData.msg_read_info_change_event;
              if (data) {
                const beemReadMsg = {
                  seq: Number(data.string_seq_id),
                  time: data.uint64_time,
                };
                emitter.emit(EVENT.READ_RECEIPT, beemReadMsg);
              }
            }
          } else if (subMsgType === 0x210 && subCommand === 0xe5) {
            // 新事件推送
            const head = scData['msg_crm_common_head'] || {};
            const crmSubCmd = head['uint32_crm_sub_cmd'];
            // 人工接待信息
            const manualInfo = scData['msg_s2c_manual_session_push'] || {};
            // 机器人接待信息
            const robotInfo = scData['msg_s2c_robot_session_push'] || {};
            // 排队信息
            const queueInfo = scData['msg_s2c_queue_info_push'] || {};
            // 正在输入
            const inputingInfo = scData['msg_s2c_input_status_sync_push'] || {};
            _log('[sc event]', scData);

            if ([28, 29, 31].indexOf(crmSubCmd) > -1) {
              // 接待变化
              const reception: any = {};
              // uint32_event_type: 1.开始事件 2.结束事件
              if (crmSubCmd === 28 && manualInfo['uint32_event_type'] === 1) {
                // 人工会话
                // reception.isRobot = false;
                // 不能保证一定有，目前也没用，先移除了。
                // reception.receptionType = manualInfo['int32_recep_type']; //  1-分组接待 2-员工接待
                reception.extuin = manualInfo['uint64_kfext']; // 接待的员工id
              } else if (
                crmSubCmd === 29 &&
                robotInfo['uint32_event_type'] === 1
              ) {
                // 机器人会话
                reception.isRobot = true;
                reception.robotId = Number(robotInfo['string_robot_id']); // 转化为数字
              } else if (
                crmSubCmd === 31 &&
                queueInfo['uint32_event_type'] === 1
              ) {
                // 进入未接入池
                reception.inWaitPool = true;
              }

              // 当有机器人或人工接待时才返回具体数据，否则返回空
              this.updateReception!(reception);
              _report({
                module: 'c-imsdk',
                method: 'onRecptionChange',
                from: _user_id,
                message: {
                  reception,
                },
              });
            } else if ([32].indexOf(crmSubCmd) > -1) {
              // 正在输入
              const ipevt = inputingInfo['uint32_event_type'];
              emitter.emit(EVENT.TYPING, { typing: ipevt === 1 });
            } else if (crmSubCmd === 33) {
              // qq 邀请
              const data = scData['msg_s2c_qq_invatation_push'] || {};
              emitter.emit(EVENT.INVITE_QQ, { data });
            } else if (crmSubCmd === 34) {
              // qq 卡片邀请
              const data = scData['msg_s2c_qq_business_card_push'] || {};
              emitter.emit(EVENT.ADD_QQ_CARD, { data });
            }
          }
        }
      }
    );
    
    // 错误处理。
    _socketio.on(Instant.NOTIFY_MESSAGE_TYPE.ERROR, (error) => {
      _report({
        module: 'c-imsdk',
        method: 'onError',
        from: _user_id,
        message: error,
      });
      emitter.emit(EVENT.ERROR, error);
    });

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: '[c-imsdk_login]',
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: '[login][done]',
      from: _user_id,
      message: '',
    });
  };

  sdk.logout = function () {
    clearTimeout(_send_sync_message_timeoutid);
    _socketio && _socketio.close();
    _sdk_is_ready = false;

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: '[c-imsdk_logout]',
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'logout',
      from: _user_id,
      message: '',
    });
  };

  // 断线后重连，TOFIXED
  sdk.reConnect = function () {
    _socketio && _socketio.createConnect();
    // 重连后要在执行一次，已触发ready
    _socketio.ready().then((info: any) => {
      _log('Instant Ready 2', info);
      _report({
        module: 'c-imsdk',
        method: 'reConnect-onReady',
        from: _user_id,
        message: `Event`,
      });
      // 通知上层:SDK已经准备好
      this.sendContactMessage!();
    });

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: '[c-imsdk_reConnect]',
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'reConnect',
      from: _user_id,
      message: '',
    });
  };

  /**
   * 文本消息组包
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {Object} params.payload - 消息体
   * @param {Array} params.payload.text - 消息文本
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.createTextMessage = function (params: any) {
    const to = Number(params.to);
    if (isNaN(to)) {
      _error('消息接收方(to)不是数值类型', params.to);
      return;
    }

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createTextMessage][${to}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createTextMessage',
      from: _user_id,
      to: to,
      message: {
        payload: params.payload,
      },
    });

    const payload = params.payload || { text: '' };
    let user_id = params.payload.user_id;
    let user_type = params.payload.user_type;
    let uint64_pubacc_id = params.payload.pubacc_id;
    let str_session_id = '';
    let info = _contact_info_map.get(to);
    if (info && info.extend) {
      user_id = info.extend.str_user_id || user_id;
      user_type = info.extend.user_type || user_type;
      str_session_id = info.extend.str_session_id || str_session_id;
      uint64_pubacc_id = info.extend.uint64_pubacc_id || uint64_pubacc_id;
    }

    return proto.createTextMessage(
      {
        aid: to,
        crm_user_type: user_type,
        str_cid: user_id,
        str_session_id: str_session_id,
        uint64_pubacc_id: uint64_pubacc_id,
      },
      payload.text,
      payload.crmData
    );
  };
  /**
   * 图片消息组包
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {Object} params.payload - 消息体
   * @param {string} params.payload.image - 消息图片
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.createImageMessage = function (params: any) {
    const to = Number(params.to);
    if (isNaN(to)) {
      _error('消息接收方(to)不是数值类型', params.to);
      return;
    }

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createImageMessage][${to}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createImageMessage',
      from: _user_id,
      to: to,
      message: {
        payload: params.payload,
      },
    });

    const payload = params.payload || { image: {} };
    let user_id = params.payload.user_id;
    let user_type = params.payload.user_type;
    let uint64_pubacc_id = params.payload.pubacc_id;
    let str_session_id = '';
    let info = _contact_info_map.get(to);
    if (info && info.extend) {
      user_id = info.extend.str_user_id || user_id;
      user_type = info.extend.user_type || user_type;
      str_session_id = info.extend.str_session_id || str_session_id;
      uint64_pubacc_id = info.extend.uint64_pubacc_id || uint64_pubacc_id;
    }
    return proto.createImageMessage(
      {
        aid: to,
        crm_user_type: user_type,
        str_cid: user_id,
        str_session_id: str_session_id,
        uint64_pubacc_id: uint64_pubacc_id,
      },
      payload.image
    );
  };
  /**
   * 获取图片上传COS地址
   * @param {Object} params
   * @param {number} params.size - 图片大小
   */
  sdk.createImageUploadMessage = function (params: any) {
    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: '[c-imsdk_createImageUploadMessage]',
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createImageUploadMessage',
      from: _user_id,
      message: '',
    });

    return proto.createMsgUploadReq(
      OBJTYPES.OBJ_IMAGE,
      params.size,
      params.name,
      typeof (params.method) === 'string' && params.method.toLowerCase() === 'post', // 是否post方式上传
      params.resStore,
    );
  };
  /**
   * 获取图片显示地址
   * @param {Object} params
   * @param {number} params.path - 图片路径
   * @param {number} params.size - 图片大小
   */
  sdk.createImageDownloadMessage = function (params: any) {
    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createImageDownloadMessage][${params.path}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createImageDownloadMessage',
      from: _user_id,
      message: {
        path: params.path,
      },
    });

    return proto.createMsgDownloadReq(
      params.path,
      OBJTYPES.OBJ_IMAGE,
      params.size,
      params.name,
      params.resStore,
      params.cosBucket,
    );
  };
  /**
   * 图文消息组包(QQ规范XML)
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {Object} params.payload - 消息体
   * @param {string} params.payload.imageText - 消息图文
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.createImageTextMessage = function (params: any) {
    const to = Number(params.to);
    if (isNaN(to)) {
      _error('消息接收方(to)不是数值类型', params.to);
      return;
    }

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createImageTextMessage][${to}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createImageTextMessage',
      from: _user_id,
      to: to,
      message: {
        payload: params.payload,
      },
    });

    const payload = params.payload || { imageText: '' };
    let user_id = params.payload.user_id;
    let user_type = params.payload.user_type;
    let uint64_pubacc_id = params.payload.pubacc_id;
    let str_session_id = '';
    let info = _contact_info_map.get(to);
    if (info && info.extend) {
      user_id = info.extend.str_user_id || user_id;
      user_type = info.extend.user_type || user_type;
      str_session_id = info.extend.str_session_id || str_session_id;
      uint64_pubacc_id = info.extend.uint64_pubacc_id || uint64_pubacc_id;
    }
    return proto.createImageTextMessage(
      {
        aid: to,
        crm_user_type: user_type,
        str_cid: user_id,
        str_session_id: str_session_id,
        uint64_pubacc_id: uint64_pubacc_id,
      },
      payload.imageText
    );
  };
  /**
   * 企点规范XML消息组包
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {Object} params.payload - 消息体
   * @param {string} params.payload.qdXml - 消息体
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.createQDXMLMessage = function (params: any) {
    const to = Number(params.to);
    if (isNaN(to)) {
      _error('消息接收方(to)不是数值类型', params.to);
      return;
    }

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createQDXMLMessage][${to}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createQDXMLMessage',
      from: _user_id,
      to: to,
      message: {
        payload: params.payload,
      },
    });

    const payload = params.payload || { qdXml: '' };
    let user_id = params.payload.user_id;
    let user_type = params.payload.user_type;
    let uint64_pubacc_id = params.payload.pubacc_id;
    let str_session_id = '';
    let info = _contact_info_map.get(to);
    if (info && info.extend) {
      user_id = info.extend.str_user_id || user_id;
      user_type = info.extend.user_type || user_type;
      str_session_id = info.extend.str_session_id || str_session_id;
      uint64_pubacc_id = info.extend.uint64_pubacc_id || uint64_pubacc_id;
    }
    return proto.createQDXMLMessage(
      {
        aid: to,
        crm_user_type: user_type,
        str_cid: user_id,
        str_session_id: str_session_id,
        uint64_pubacc_id: uint64_pubacc_id,
      },
      payload.qdXml,
      payload.crmData,
    );
  };
  /**
   * 语音消息组包
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {Object} params.payload - 消息体
   * @param {string} params.payload.ptt - 消息语音
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.createVoiceMessage = function (params: any) {
    const to = Number(params.to);
    if (isNaN(to)) {
      _error('消息接收方(to)不是数值类型', params.to);
      return;
    }

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createVoiceMessage][${to}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createVoiceMessage',
      from: _user_id,
      to: to,
      message: {
        payload: params.payload,
      },
    });

    const payload = params.payload || { file: {} };
    let user_id = params.payload.user_id;
    let user_type = params.payload.user_type;
    let uint64_pubacc_id = params.payload.pubacc_id;
    let str_session_id = '';
    let info = _contact_info_map.get(to);
    if (info && info.extend) {
      user_id = info.extend.str_user_id || user_id;
      user_type = info.extend.user_type || user_type;
      str_session_id = info.extend.str_session_id || str_session_id;
      uint64_pubacc_id = info.extend.uint64_pubacc_id || uint64_pubacc_id;
    }
    return proto.createPttMessage(
      {
        aid: to,
        crm_user_type: user_type,
        str_cid: user_id,
        str_session_id: str_session_id,
        uint64_pubacc_id: uint64_pubacc_id,
      },
      payload.ptt
    );
  };
  /**
   * 文件消息组包
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {Object} params.payload - 消息体
   * @param {string} params.payload.file - 消息文件
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.createFileMessage = function (params: any) {
    const to = Number(params.to);
    if (isNaN(to)) {
      _error('消息接收方(to)不是数值类型', params.to);
      return;
    }

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createFileMessage][${to}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createFileMessage',
      from: _user_id,
      to: to,
      message: {
        payload: params.payload,
      },
    });

    const payload = params.payload || { file: {} };
    let user_id = params.payload.user_id;
    let user_type = params.payload.user_type;
    let uint64_pubacc_id = params.payload.pubacc_id;
    let str_session_id = '';
    let info = _contact_info_map.get(to);
    if (info && info.extend) {
      user_id = info.extend.str_user_id || user_id;
      user_type = info.extend.user_type || user_type;
      str_session_id = info.extend.str_session_id || str_session_id;
      uint64_pubacc_id = info.extend.uint64_pubacc_id || uint64_pubacc_id;
    }
    return proto.createFileMessage(
      {
        aid: to,
        crm_user_type: user_type,
        str_cid: user_id,
        str_session_id: str_session_id,
        uint64_pubacc_id: uint64_pubacc_id,
      },
      payload.file
    );
  };
  /**
   * 获取文件上传COS地址
   * @param {Object} params
   * @param {number} params.size - 文件大小
   *
   * @param {Object} objType - cos对象类型
   */
  sdk.createFileUploadMessage = function (params: any, objType: OBJTYPES) {
    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: '[c-imsdk_createFileUploadMessage]',
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createFileUploadMessage',
      from: _user_id,
      message: '',
    });

    return proto.createMsgUploadReq(
      objType || OBJTYPES.OBJ_FILE,
      params.size,
      params.name,
      typeof (params.method) === 'string' && params.method.toLowerCase() === 'post', // 是否post方式上传
      params.resStore,
    );
  };
  /**
   * 获取文件地址
   * @param {Object} params
   * @param {number} params.path - 文件路径
   * @param {number} params.size - 文件大小
   *
   * @param {Object} objType - cos对象类型
   */
  sdk.createFileDownloadMessage = function (params: any, objType: OBJTYPES) {
    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createFileDownloadMessage][${params.path}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createFileDownloadMessage',
      from: _user_id,
      message: {
        path: params.path,
      },
    });

    return proto.createMsgDownloadReq(
      params.path,
      objType || OBJTYPES.OBJ_FILE,
      params.size || 0,
      params.name || '',
      params.resStore,
      params.cosBucket,
    );
  };
  /**
   * 创建长文本消息
   * @param message 消息发送包
   */
  sdk.createLongTextMessage = function (params: any) {
    const to = Number(params.to);
    if (isNaN(to)) {
      _error('消息接收方(to)不是数值类型', params.to);
      return;
    }

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createLongTextMessage][${to}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createLongTextMessage',
      from: _user_id,
      to: to,
      message: {
        payload: params.payload,
      },
    });

    const payload = params.payload || { long_msg: {} };
    let user_id = params.payload.user_id;
    let user_type = params.payload.user_type;
    let uint64_pubacc_id = params.payload.pubacc_id;
    let str_session_id = '';
    let info = _contact_info_map.get(to);
    if (info && info.extend) {
      user_id = info.extend.str_user_id || user_id;
      user_type = info.extend.user_type || user_type;
      str_session_id = info.extend.str_session_id || str_session_id;
      uint64_pubacc_id = info.extend.uint64_pubacc_id || uint64_pubacc_id;
    }
    return proto.createLongTextMessage(
      {
        aid: to,
        crm_user_type: user_type,
        str_cid: user_id,
        str_session_id: str_session_id,
        uint64_pubacc_id: uint64_pubacc_id,
      },
      payload.long_msg,
      payload.crmData
    );
  }
  /**
   * 消息发送
   * @param {Object} message - 消息发送包
   */
  sdk.sendMessage = function (message) {
    if (!this.sessionStarted) {
      // 如果尚未成功进线。则禁止发送
      _report({
        module: 'c-imsdk',
        method: '[sendMessage][error]',
        from: _user_id,
        message: 'not-sessionStarted',
      });

      return Promise.resolve({
        code: -9999,
        error: '接入会话失败，无法发送消息',
      });
    }

    _report({
      module: 'c-imsdk',
      method: '[sendMessage][request]',
      from: _user_id,
      message: message,
    });

    return _socketio
      .send(
        {
          uint32_sub_command: Instant.MESSAGE_TYPE.MESSAGE,
        },
        message
      )
      .then((data: any) => {
        const result = proto.unpackMsgSendRsp(data);
        if (result.syncseq - _sync_messsage_seq == 1) {
          _sync_messsage_seq = result.syncseq; // 更新_sync_messsage_seq
          if (_contact_info_map.has(result.to)) {
            let info = _contact_info_map.get(result.to);
            info.seq = Math.max(info.seq, result.seq || 0); // 更新roaming_seq
            info.msgtime = result.time;
            info.unread = 1;
            if (result.content.length > 0) {
              let item = result.content[0];
              info.summary = this.createMessageSummary!(item);
            }
            this.setMessageRead!({ to: result.to });
          }
        } else {
          // 此时_sync_messsage_seq不连续，存在空洞，主动拉取一次
          this.sendSyncMessage!(_sync_messsage_seq + 1, []);
        }

        _log('发送消息回包:', data);

        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: `[c-imsdk_sendMessage_rsp][${JSON.stringify(result)}]`,
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[sendMessage][response]',
          from: _user_id,
          message: result,
        });

        return result;
      })
      .catch((err: any) => {
        _error('发送消息超时:', err);

        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 2, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: '[c-imsdk_sendMessage_tiemout]',
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[sendMessage][timeout]',
          from: _user_id,
          message: {
            err,
            message,
          },
        });

        throw {
          reason: err.result,
          seq: err.seq,
        };
      });
  };

  /**
   * 消息重发
   * @param {number} resendSeq - 重发消息seq
   */
  sdk.resendMessage = function (resendSeq) {
    if (!this.sessionStarted) {
      // 如果尚未成功进线。则禁止发送。注意这里处理和send不一样。。
      _report({
        module: 'c-imsdk',
        method: 'resendMessage',
        from: _user_id,
        message: `[error]not-sessionStarted`,
      });

      return Promise.reject();
    }

    _report({
      module: 'c-imsdk',
      method: '[resendMessage][request]',
      from: _user_id,
      message: {
        resendSeq,
      },
    });

    return _socketio
      .resend(resendSeq)
      .then((data: any) => {
        const result = proto.unpackMsgSendRsp(data);
        if (result.syncseq - _sync_messsage_seq == 1) {
          _sync_messsage_seq = result.syncseq; // 更新_sync_messsage_seq
          if (_contact_info_map.has(result.to)) {
            let info = _contact_info_map.get(result.to);
            info.seq = Math.max(info.seq, result.seq || 0); // 更新roaming_seq
            info.msgtime = result.time;
            info.unread = 1;
            if (result.content.length > 0) {
                let item = result.content[0];
                info.summary = this.createMessageSummary!(item);
            }
            this.setMessageRead!({ to: result.to });
          }
        } else {
          // 此时_sync_messsage_seq不连续，存在空洞，主动拉取一次
          this.sendSyncMessage!(_sync_messsage_seq + 1, []);
        }

        _log('重发消息回包:', data);
        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: `[c-imsdk_resendMessage_rsp][${JSON.stringify(
                  result
                )}]`,
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[resendMessage][response]',
          from: _user_id,
          message: {
            resendSeq,
            result,
          },
        });

        return result;
      })
      .catch((err: any) => {
        _error('重发消息超时:', err);
        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 2, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: '[c-imsdk_resendMessage_tiemout]',
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[resendMessage][timeout]',
          from: _user_id,
          message: {
            resendSeq,
            err,
          },
        });

        throw {
          reason: err.result,
          seq: err.seq,
        };
      });
  };
  /**
   * 消息撤回
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {Object} params.payload - 消息体
   * @param {Array}  params.payload.message - 消息
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.revokeMessage = function (params: any) {
    _log('撤回消息', params);

    const to = Number(params.to);

    let user_id = params.payload.user_id;
    let user_type = params.payload.user_type;
    let uint64_pubacc_id = params.payload.pubacc_id;
    let str_session_id = '';
    let info = _contact_info_map.get(to);
    if (info && info.extend) {
      user_id = info.extend.str_user_id || user_id;
      user_type = info.extend.user_type || user_type;
      str_session_id = info.extend.str_session_id || str_session_id;
      uint64_pubacc_id = info.extend.uint64_pubacc_id || uint64_pubacc_id;
    }

    const revokeMessage = params.payload.message;
    let message = proto.createRevokeMessage(
      {
        aid: to,
        crm_user_type: user_type,
        str_cid: user_id,
        str_session_id: str_session_id,
        uint64_pubacc_id: uint64_pubacc_id,
      },
      {
        uint64_msg_roaming_seq: revokeMessage.seq,
        uint64_msg_sync_seq: revokeMessage.syncseq,
        uint64_side_sync_seq: revokeMessage.sidesyncseq,
        uint64_msg_time: revokeMessage.time * 1000,
      }
    );
    _report({
      module: 'c-imsdk',
      method: '[revokeMessage][request]',
      from: _user_id,
      message: message,
    });
    return _socketio
      .send(
        {
          uint32_sub_command: Instant.MESSAGE_TYPE.MESSAGE,
        },
        message
      )
      .then((data: any) => {
        const result = proto.unpackMsgSendRsp(data);

        _log('撤回消息回包:', data);
        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: `[c-imsdk_revokeMessage_rsp][${JSON.stringify(
                  result
                )}]`,
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[revokeMessage][response]',
          from: _user_id,
          message: result,
        });

        return result;
      })
      .catch((err: any) => {
        _error('撤回消息超时:', err);
        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 2, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: '[c-imsdk_revokeMessage_tiemout]',
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[revokeMessage][timeout]',
          from: _user_id,
          message: err,
        });

        throw {
          reason: err.result,
          seq: err.seq,
        };
      });
  };

  sdk.onMessageNotify = function (result: any) {
    _log('同步消息推送', result);
    if (_sdk_is_ready) {
      let seq = proto.unpackNewMsgNotice(result.message);

      Mango({
        report_content: {
          common_head: {
            uint64_kfuin: _socketio.config.kfuin, // 主号
            uint64_aid: _socketio.config.aid, // 工号
            uint64_client_time: new Date().getTime(),
            uint32_client_type: _socketio.config.client_type,
            uint32_appid: _socketio.config.appid,
            str_client_version: '1.0.0',
          },
          log_event: [
            {
              log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
              str_log: `[c-imsdk_onMessageNotify][sync_seq:${_sync_messsage_seq}][seq:${seq}]`,
            },
          ],
        },
      });

      _report({
        module: 'c-imsdk',
        method: 'onMessageNotify',
        from: _user_id,
        message: {
          localseq: _sync_messsage_seq,
          msgseq: seq,
        },
      });

      if (_sync_messsage_seq > seq || _send_sync_loading) {
        _next_sync_notify_seq = Math.max(_next_sync_notify_seq, seq);
        return;
      }
      // 收到消息推送，对于同步消息定时器计数减1
      _send_sync_timeout_count = Math.max(_min_send_sync_timeout_count, --_send_sync_timeout_count);

      sdk.sendSyncMessage!(_sync_messsage_seq + 1, []); // !!!注意:这里要对seq加1处理，不然的话会有重复消息
    }
  };

  /**
   * 新消息推送后同步消息发送
   */
  sdk.sendSyncMessage = function (seq, syncMessage: any = []) {
    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_sendSyncMessage_req][${seq}]`,
          },
        ],
      },
    });

    _send_sync_loading = true;

    _report({
      module: 'c-imsdk',
      method: '[sendSyncMessage][request]',
      from: _user_id,
      message: {
        seq,
      },
    });

    if (syncMessage.length === 0) {
      /**
       * 同步消息定时器的作用:
       * 防止新消息推送包在网络中丢失
       */
      clearTimeout(_send_sync_message_timeoutid);
      let milliseconds = _max_send_sync_timeout / Math.pow(2, _send_sync_timeout_count);
      if (_send_sync_timeout_count >= _max_send_sync_timeout_count) {
        // 到这里的逻辑说明svr可能出问题了，把定时器时长随机散布在3s到10s之间
        // 防止svr长时间不能恢复，全网出现共振的情况
        const max = 10 * 1000, min = 3 * 1000;
        milliseconds = Math.round(min + Math.random() * (max - min));
        _report({
            module: 'c-imsdk',
            method: '[sendSyncMessage][warning]',
            from: _user_id,
            message: {
              seq,
            },
        })
      }
      _send_sync_message_timeoutid = setTimeout(() => {
        this.sendSyncMessage!(_sync_messsage_seq + 1, []);
        // 如果同步定时器调用一次，计数加1
        _send_sync_timeout_count = Math.min(_max_send_sync_timeout_count, ++_send_sync_timeout_count);
      }, milliseconds);
    }

    let message = proto.createSyncMessage(seq, this.config.syncMsgSize);
    _socketio
      .send(
        {
          uint32_sub_command: Instant.MESSAGE_TYPE.SYNC_MESSAGE,
        },
        message
      )
      .then((data: any) => {
        _log('同步消息回包:', data);
        _report({
          module: 'c-imsdk',
          method: '[sendSyncMessage][response][success]',
          from: _user_id,
        });

        let result = proto.unpackMsgSyncRsp(data);
        if (result.length > 0) {
          // 同步消息去重和空洞处理(后台返回的数据单调递增)
          for (let i = 0; i < result.length; i++) {
            let item = result[i];
            if (item.syncseq - _sync_messsage_seq == 1) {
              syncMessage.push(item);
              _sync_messsage_seq = item.syncseq;
              if (_syncseq_hole_map.has(item.syncseq)) {
                // 如果前面有空洞存在，这里表示已经拉到了
                _syncseq_hole_map.delete(item.syncseq);
              }
              _report({
                module: 'c-imsdk',
                method: '[sendSyncMessage][response][push]',
                from: _user_id,
                message: {
                  msgseq: item.syncseq,
                },
              });

              if (item.type === TYPES.MSG_GRAY) {
                // 灰条消息，如果含有工号信息(此时为转接提示)，就主动拉取一次接入状态。
                try {
                  const extuin = item.crm.msg_webim_extra_data.uint64_extuin;
                  if (extuin) {
                    this.getReceptionStatus!();
                  }
                }catch(ex){}
              }
            } else if (item.syncseq - _sync_messsage_seq > 1) {
              const _hole_key = _sync_messsage_seq + 1;
              if (_syncseq_hole_map.has(_hole_key)) {
                let count = _syncseq_hole_map.get(_hole_key);
                if (count >= 2) {
                  // 空洞计数大于2就跳过，防止空洞数据一直没有。
                  // 这里将当前值设置为空洞的key，跳过空洞。
                  _sync_messsage_seq = _hole_key;
                  _report({
                    module: 'c-imsdk',
                    method: '[sendSyncMessage][response][hole-lost]',
                    from: _user_id,
                    message: {
                      msgseq: item.syncseq,
                      skipseq: _sync_messsage_seq,
                    },
                  });
                  // 这里不能 continue。因为前面已经绕过了空洞了，所以应该继续后面的流程，拉取一下下一条消息。
                  // 其实也不用重新拉取，可以 i-=1; continue; 这样就会按照新的 _sync_messsage_seq 来对齐数据。
                  // 但是这样会使过程变的更复杂，更容易出现问题，因此还是break;然后重新请求吧。 
                  // continue; 
                }
                _syncseq_hole_map.set(_hole_key, ++count);
              } else {
                _syncseq_hole_map.set(_hole_key, 1);
                // 仅中第一次重试时上报，防止重复上报
                _report({
                  module: 'c-imsdk',
                  method: '[sendSyncMessage][response][hole]',
                  from: _user_id,
                  message: {
                    msgseq: item.syncseq,
                    localseq: _sync_messsage_seq,
                  },
                });  
              }
              // 空洞处理: 1、丢弃不连续的消息，
              result = result.slice(0, i);
              // 2、1s后从当前连续处重新请求一次
              setTimeout(() => {
                this.sendSyncMessage &&
                  this.sendSyncMessage(_sync_messsage_seq + 1, []);
              }, 1000);

              _log('同步消息回包[空洞hole]:', item.syncseq);

              Mango({
                report_content: {
                  common_head: {
                    uint64_kfuin: _socketio.config.kfuin, // 主号
                    uint64_aid: _socketio.config.aid, // 工号
                    uint64_client_time: new Date().getTime(),
                    uint32_client_type: _socketio.config.client_type,
                    uint32_appid: _socketio.config.appid,
                    str_client_version: '1.0.0',
                  },
                  log_event: [
                    {
                      log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                      str_log: `[c-imsdk_sendSyncMessage_req][hole][${item.syncseq}]`,
                    },
                  ],
                },
              });
              break;
            } else {
              // 重复数据
              _log('----------->same-seq!', item.syncseq, _sync_messsage_seq);
              _report({
                module: 'c-imsdk',
                method: '[sendSyncMessage][response][repeat]',
                from: _user_id,
                message: {
                  syncseq: item.syncseq,
                },
              });
              continue;
            }
          }
        } else {
          // 拉取到的同步消息数据为0，对于同步消息定时器计数减1
          _send_sync_timeout_count = Math.max(_min_send_sync_timeout_count, --_send_sync_timeout_count);
        }

        if (result.length < this.config.syncMsgSize!) {
          let fromMessageMap: any = {};
          for (const item of syncMessage) {
            // 1、过滤无效的数据
            if (!item.content && !item.path) {
              _report({
                module: 'c-imsdk',
                method: '[sendSyncMessage][response][empty]',
                from: _user_id,
                message: item,
              });
              continue;
            }
            // 2、将相同的from数据进行归并
            let from = item.from;
            if (item.from == _user_id) {
              // svr下发的B2C或者多端的情况
              from = item.to;
            }

            fromMessageMap[from] = fromMessageMap[from] || [];
            fromMessageMap[from].push(item);

            // 3、收到新消息推送后,需要更新_contact_info_map的信息
            let summary = this.createMessageSummary!(item);
            if (_contact_info_map.has(from)) {
              let info = _contact_info_map.get(from);
              if (item.seq > info.seq) {
                // 新消息的seq大于已有的seq才需要未读计数
                info.summary = summary;
                info.seq = item.seq;
                info.unread += 1;
                info.msgtime = item.time;
                if (item.crm) {
                  info.extend.str_session_id = '';
                  info.extend.str_user_id = item.crm['str_cid'];
                  info.extend.user_type = item.crm['crm_user_type'];
                  info.extend.uint64_pubacc_id =
                    item.crm['uint64_pubacc_id'] || 0;
                }
              }
            } else {
              let info = {
                context: '',
                id: from,
                type: 0,
                summary: summary,
                seq: item.seq,
                unread: 1,
                msgtime: item.time,
                block: 0,
                top: 0,
                toptime: 0,
                extend: {},
              };
              if (item.crm) {
                info.extend = {
                  str_session_id: '',
                  str_user_id: item.crm['str_cid'],
                  user_type: item.crm['crm_user_type'],
                  uint64_pubacc_id: item.crm['uint64_pubacc_id'] || 0,
                };
              }
              _contact_info_map.set(from, info);
            }
          }

          if (syncMessage.length > 0) {
            emitter.emit(EVENT.MESSAGE_RECEIVED, fromMessageMap);
            let receivedMsg = {};
            try {
              Object.keys(fromMessageMap).forEach((key: any) => {
                const fromMsg = fromMessageMap[key];
                receivedMsg[key] = [];
                fromMsg.forEach(item => {
                  receivedMsg[key].push({
                    seq: item.syncseq, content: item.content, path: item.path, from: item.from, to: item.to
                  })
                });
              });
            } catch (ex: any) { _error(ex) }
            _log('receivedMsg', receivedMsg);
            _report({
              module: 'c-imsdk',
              method: 'MESSAGE_RECEIVED',
              from: _user_id,
              message: { receivedMsg },
            });
            this.updateUnReadMessage!();
          }

          _log('sync_message_array', syncMessage);

          _send_sync_loading = false;
          if (_next_sync_notify_seq > _sync_messsage_seq) {
            // 在同步消息请求过程中又收到了推送
            this.sendSyncMessage!(_sync_messsage_seq + 1, []);
          }

        } else {
          this.sendSyncMessage!(_sync_messsage_seq + 1, syncMessage); // !!!注意:这里要对seq加1处理，不然的话会有重复消息
        }

        let _log_data: any = [];
        // if (syncMessage.length > 0) {
        //     let _maxSync = syncMessage[syncMessage.length - 1];
        //     let _minSync = syncMessage[0];
        //     _log_data.push({
        //         int64_min_sync_id: _minSync.syncseq,
        //         int64_max_sync_id: _maxSync.syncseq,
        //         int64_from_id: _maxSync.from,
        //         int64_to_id: _maxSync.to,
        //         int64_msg_send_time: _maxSync.time * 1000,
        //         int64_user_login_time: data.login_time,
        //         int64_msg_recv_server_time: data.timestamp,
        //         uint64_gseq: data.gseq,
        //     });
        // }
        for (const item of result) {
          _log_data.push({
            seq: item.seq,
            syncseq: item.syncseq,
            from: item.from,
            to: item.to,
            time: item.time,
            type: item.type || '',
          });
        }

        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            // sync_event: _log_data,
            log_event: [
              {
                log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: `[c-imsdk_sendSyncMessage_rsp][${seq}][${JSON.stringify(
                  _log_data
                )}]`,
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[sendSyncMessage][response][done]',
          from: _user_id,
          message: {
            seq,
            msginfo: _log_data,
          },
        });
      })
      .catch((err: any) => {
        _send_sync_loading = false;

        _error('同步消息超时:', err);

        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 2, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: `[c-imsdk_sendSyncMessage_timeout][${seq}]`,
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[sendSyncMessage][timeout]',
          from: _user_id,
          message: {
            seq,
            err,
          },
        });
      });
  };

  /**
   * 生成消息摘要
   * @param {Object} message
   */
  sdk.createMessageSummary = function (message: any) {
    let summary = '[未知消息类型]';
    if (message.type == TYPES.MSG_TEXT) {
      summary = message.content.substr(0, 20);
    } else if (message.type == TYPES.MSG_FACE) {
      summary = '[表情]';
    } else if (message.type == TYPES.MSG_MEDIA) {
      summary = '[资源链接]';
    } else if (message.type == TYPES.MSG_IMAGE) {
      summary = '[图片]';
    } else if (message.type == TYPES.MSG_VOICE) {
      summary = '[语音]';
    } else if (message.type == TYPES.MSG_LONG_MSG) {
      summary = '[长文本]';
    } else if (message.type == TYPES.MSG_IMAGE_TEXT) {
      summary = '[图文]';
    } else if (message.type == TYPES.MSG_QDXML) {
      summary = '[卡片]';
    } else if (message.type == TYPES.MSG_FILE) {
      summary = '[文件]';
    } else if (message.type == TYPES.MSG_GRAY) {
      summary = '[其他消息]';
    } else if (message.type == TYPES.MSG_REVOKE) {
      if (message.from == _user_id) {
        summary = '你撤回了一条消息';
      } else {
        summary = '对方撤回了一条消息';
      }
    } else if (message.type == TYPES.MSG_MIX) {
      const subItem = message.content[0];
      if (subItem.type == TYPES.MSG_TEXT) {
        summary = subItem.content.substr(0, 20);
      } else if (subItem.type == TYPES.MSG_FACE) {
        summary = '[表情]';
      } else if (subItem.type == TYPES.MSG_MEDIA) {
        summary = '[资源链接]';
      } else if (subItem.type == TYPES.MSG_IMAGE) {
        summary = '[图片]';
      }
    } else if (message.type == TYPES.MSG_RTC) {
      summary = `${message.content.str_text_msg}-${message.content.uint32_video_status_type}`
    }

    return summary;
  };

  /**
   * 拉取漫游消息请求
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {number} params.seq - 消息起始seq
   * @param {Object} params.payload - 消息体
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.getMessageList = function (params: any) {
    const to = Number(params.to);
    let seq = Number(params.seq);
    const payload = params.payload;
    if (isNaN(to) || isNaN(seq)) {
      _error('消息接收方(to)或者起始seq不是数值类型', params.to, params.seq);
      return;
    }

    _report({
      module: 'c-imsdk',
      method: '[getMessageList][request]',
      from: _user_id,
      message: {
        to,
        seq,
      },
    });

    if (seq < 0) {
      // 第一次拉取
      seq = Number.MAX_SAFE_INTEGER
    }
    if (_contact_info_map.has(to)) {
      const info = _contact_info_map.get(to);
      if (!info.extend.str_user_id || !info.extend.user_type) {
        info.extend.str_user_id = payload.user_id;
        info.extend.user_type = payload.user_type || this.config.channelType;
        info.extend.uint64_pubacc_id = payload.pubacc_id;
      }
    } else {
      const info = {
        context: '',
        id: to,
        type: 0,
        summary: '',
          seq: 0,
        unread: 0,
        msgtime: Date.now(),
        block: 0,
        top: 0,
        toptime: 0,
        extend: {
          str_session_id: '',
          str_user_id: payload.user_id,
          // 给默认值
          user_type: payload.user_type || this.config.channelType,
          uint64_pubacc_id: payload.pubacc_id,
        },
      };
      _contact_info_map.set(to, info);
  }
    return new Promise((resolve, reject) => {
      let message = proto.createMsgRoamingReq(
        to,
        seq,
        this.config.roamingMsgSize
      );
      _socketio
        .send(
          {
            uint32_sub_command: Instant.MESSAGE_TYPE.ROAMING_MESSAGE,
          },
          message
        )
        .then((data: any) => {
          let result: any = [];
          if (data['msg_roaming_rsp']) {
            result = proto.unpackMsgRoamingRsp(data);
            if (result.length > 0) {
              seq = result[result.length - 1].seq - 1; // !!!注意:这里要对seq减1处理，不然的话会有重复消息
            }
          }

          resolve({
            messageList: result,
            nextReqMessageSeq: seq,
            isCompleted:
              result.length < this.config.roamingMsgSize! ? true : false,
          });

          _log('漫游消息回包:', data);
          let _log_data: any = [];
          if (result.length > 0) {
            let _maxRoam = result[result.length - 1];
            let _minRoam = result[0];
            _log_data.push({
              int64_min_roaming_id: _minRoam.seq,
              int64_max_roaming_id: _maxRoam.seq,
              int64_from_id: _maxRoam.from,
              int64_to_id: _maxRoam.to,
              int64_msg_send_time: _maxRoam.time * 1000,
              int64_user_login_time: data.login_time,
              int64_msg_recv_server_time: data.timestamp,
              uint64_gseq: data.gseq,
            });
          }

          Mango({
            report_content: {
              common_head: {
                uint64_kfuin: _socketio.config.kfuin, // 主号
                uint64_aid: _socketio.config.aid, // 工号
                uint64_client_time: new Date().getTime(),
                uint32_client_type: _socketio.config.client_type,
                uint32_appid: _socketio.config.appid,
                str_client_version: '1.0.0',
              },
              roaming_event: _log_data,
              log_event: [
                {
                  log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                  str_log: `[c-imsdk_getMessageList_rsp][${to}][${seq}]`,
                },
              ],
            },
          });

          _report({
            module: 'c-imsdk',
            method: '[getMessageList][response]',
            from: _user_id,
            to: to,
            message: {
              to,
              seq,
              msginfo: _log_data,
            },
          });
        })
        .catch((err: any) => {
          _error('漫游消息超时:', err);

          Mango({
            report_content: {
              common_head: {
                uint64_kfuin: _socketio.config.kfuin, // 主号
                uint64_aid: _socketio.config.aid, // 工号
                uint64_client_time: new Date().getTime(),
                uint32_client_type: _socketio.config.client_type,
                uint32_appid: _socketio.config.appid,
                str_client_version: '1.0.0',
              },
              log_event: [
                {
                  log_level: 2, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                  str_log: `[c-imsdk_getMessageList_timeout][${to}][${seq}]`,
                },
              ],
            },
          });

          _report({
            module: 'c-imsdk',
            method: '[getMessageList][tiemout]',
            from: _user_id,
            message: err,
          });

          throw {
            reason: err.result,
            seq: err.seq,
          };
        });
    });
  };

  /**
   * 全体未读消息请求
   */
  sdk.sendContactMessage = function (context = '', seq = 0) {
    _report({
      module: 'c-imsdk',
      method: '[sendContactMessage][request]',
      from: _user_id,
      message: seq,
    });

    let message = proto.createMsgContactReq(
      context,
      seq,
      this.config.contactMsgSize
    );
    _socketio
      .send(
        {
          uint32_sub_command: Instant.MESSAGE_TYPE.CONTACT_MESSAGE,
        },
        message
      )
      .then((data: any) => {
        _log('全体未读消息回包:', data);
        let result = proto.unpackMsgContactRsp(data);
        context = result.str_data_context;
        // 当本地 _sync_messsage_seq 为0时才使用contact的值
        if (_sync_messsage_seq <= 0) {
          _sync_messsage_seq = result.uint64_max_sync_seq || 0; // 找出最大的同步消息id用于下面的同步消息拉取
        }
        let contact = result.contact;
        if (contact.length > 0) {
          // context = result[result.length - 1].context;
          // seq = result[result.length - 1].seq;
          _contact_message_array = _contact_message_array.concat(contact);
        }
        if (contact.length < this.config.contactMsgSize!) {
          _log('_contact_message_array', _contact_message_array);

          // 准备好联系人信息映射表
          for (const item of _contact_message_array) {
            _contact_info_map.set(item.id, item);
            // _sync_messsage_seq = Math.max(item.syncseq || 0, _sync_messsage_seq); // 找出最大的同步消息id用于下面的同步消息拉取
          }

          _sdk_is_ready = true;
          // 通知上层:有未读消息
          this.updateUnReadMessage!();
          this.sendSyncMessage!(_sync_messsage_seq + 1, []); // 主动发起一次同步消息拉取,防止出现新消息推送丢包的情况
          // 通知上层:SDK已经准备好
          emitter.emit(EVENT.SDK_READY);

          _report({
            module: 'c-imsdk',
            method: '[sendContactMessage][response]',
            from: _user_id,
            message: result,
          });
        } else {
          this.sendContactMessage!(context, seq + this.config.contactMsgSize!);
        }
      })
      .catch((err: any) => {
        _error('全体未读消息超时:', err);

        _report({
          module: 'c-imsdk',
          method: '[sendContactMessage][timeout]',
          from: _user_id,
          message: `timeout`,
        });
      });
  };

  /**
   * 将某会话下所有未读消息已读上报
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   */
  sdk.setMessageRead = function (params: any) {
    const to = Number(params.to);
    if (!_contact_info_map.has(to)) {
      return;
    }
    const info = _contact_info_map.get(to);
    if (info.unread == 0) {
      return;
    }

    // 防止已读消息上报丢包,本地强制归0
    info.unread = 0;

    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_setMessageRead_req][${info.id}][${info.seq}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: '[setMessageRead][request]',
      from: _user_id,
      message: {
        id: info.id,
        seq: info.seq,
      },
    });

    return new Promise((resolve, reject) => {
      let message = proto.createMsgReadReq(info.id, 0, info.seq);
      _socketio
        .send(
          {
            uint32_sub_command: Instant.MESSAGE_TYPE.READ_MESSAGE,
          },
          message
        )
        .then((data: any) => {
          let result = proto.unpackMsgReadRsp(data);
          resolve(result);

          _log('已读上报消息回包:', data);
          Mango({
            report_content: {
              common_head: {
                uint64_kfuin: _socketio.config.kfuin, // 主号
                uint64_aid: _socketio.config.aid, // 工号
                uint64_client_time: new Date().getTime(),
                uint32_client_type: _socketio.config.client_type,
                uint32_appid: _socketio.config.appid,
                str_client_version: '1.0.0',
              },
              log_event: [
                {
                  log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                  str_log: `[c-imsdk_setMessageRead_rsp][${info.id}][${info.seq}]`,
                },
              ],
            },
          });
          _report({
            module: 'c-imsdk',
            method: '[setMessageRead][response]',
            from: _user_id,
            message: {
              id: info.id,
              seq: info.seq,
            },
          });
        })
        .catch((err: any) => {
          _error('已读上报消息超时:', err);
          Mango({
            report_content: {
              common_head: {
                uint64_kfuin: _socketio.config.kfuin, // 主号
                uint64_aid: _socketio.config.aid, // 工号
                uint64_client_time: new Date().getTime(),
                uint32_client_type: _socketio.config.client_type,
                uint32_appid: _socketio.config.appid,
                str_client_version: '1.0.0',
              },
              log_event: [
                {
                  log_level: 2, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                  str_log: `[c-imsdk_setMessageRead_timeout][${info.id}][${info.seq}]`,
                },
              ],
            },
          });
          _report({
            module: 'c-imsdk',
            method: '[setMessageRead][timeout]',
            from: _user_id,
            message: {
              id: info.id,
              seq: info.seq,
            },
          });
        });
    });
  };

  /**
   * 通知上层: 更新未读消息
   */
  sdk.updateUnReadMessage = function () {
    let contact: any = [];
    _contact_info_map.forEach((item) => {
      if (item.unread > 0) {
        contact.push({
          id: item.id,
          content: item.summary,
          time: item.msgtime,
          unread: item.unread,
          extend: {
            user_type: item.extend && item.extend.user_type,
            user_id: item.extend && item.extend.str_user_id,
            // session_id: item.extend && item.extend.str_session_id
          },
        });
      }
    });
    if (contact.length > 0) {
      emitter.emit(EVENT.MESSAGE_UNREAD_UPDATED, contact);

      let _log_data: any = [];
      for (const _contact of contact) {
        _log_data.push({
          id: _contact.id,
          // type: _contact.extend.user_type,
          // userid: _contact.extend.user_id,
          unread: _contact.unread,
          // time: _contact.time
        });
      }
      Mango({
        report_content: {
          common_head: {
            uint64_kfuin: _socketio.config.kfuin, // 主号
            uint64_aid: _socketio.config.aid, // 工号
            uint64_client_time: new Date().getTime(),
            uint32_client_type: _socketio.config.client_type,
            uint32_appid: _socketio.config.appid,
            str_client_version: '1.0.0',
          },
          log_event: [
            {
              log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
              str_log: `[c-imsdk_updateUnReadMessage][${JSON.stringify(
                _log_data
              )}]`,
            },
          ],
        },
      });
      _report({
        module: 'c-imsdk',
        method: 'updateUnReadMessage',
        from: _user_id,
        message: {
          msginfo: _log_data,
        },
      });
    }
  };

  /**
   * 获取对象上传接口/下载地址
   * @param {Object} message - 消息发送包
   */
  sdk.sendUploadOrDownloadMessage = function (message) {
    _report({
      module: 'c-imsdk',
      method: '[sendUploadOrDownloadMessage][request]',
      from: _user_id,
      message: message,
    });

    return _socketio
      .send(
        {
          uint32_sub_command: Instant.MESSAGE_TYPE.PRESIGNED_URL,
        },
        message
      )
      .then((data: any) => {
        const result = proto.unpackMsgUploadOrDownloadRsp(data);

        _log('文件上传/下载回包:', data);
        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: `[c-imsdk_sendUploadOrDownloadMessage_rsp][${JSON.stringify(
                  result
                )}]`,
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[sendUploadOrDownloadMessage][response]',
          from: _user_id,
          message: result,
        });

        return result;
      })
      .catch((err: any) => {
        _error('文件上传/下载超时:', err);
        Mango({
          report_content: {
            common_head: {
              uint64_kfuin: _socketio.config.kfuin, // 主号
              uint64_aid: _socketio.config.aid, // 工号
              uint64_client_time: new Date().getTime(),
              uint32_client_type: _socketio.config.client_type,
              uint32_appid: _socketio.config.appid,
              str_client_version: '1.0.0',
            },
            log_event: [
              {
                log_level: 2, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
                str_log: '[c-imsdk_sendUploadOrDownloadMessage_timeout]',
              },
            ],
          },
        });

        _report({
          module: 'c-imsdk',
          method: '[sendUploadOrDownloadMessage][tiemout]',
          from: _user_id,
          message: err,
        });

        throw {
          reason: err.result,
          seq: err.seq,
        };
      });
  };

  // 进线接入处理
  sdk.clientIncoming = function (params: any) {
    const message = proto.createMsgClientIncomingReq(params);

    // 超时处理
    const timer = setTimeout(() => {
      emitter.emit(EVENT.LOCAL_NOTIFY, {
        type: 'session_start_timeout',
        content: '会话接入超时',
      });

      _report({
        module: 'c-imsdk',
        method: '[clientIncoming][timeout]',
        from: _user_id,
        message: 'timeout',
      });
    }, 60 * 1000);

    _report({
      module: 'c-imsdk',
      method: '[clientIncoming][request]',
      from: _user_id,
      message: params,
    });

    this.sendCmdMessage!(325001, message).then((data: any) => {
      // 清理超时处理
      clearTimeout(timer);

      _report({
        module: 'c-imsdk',
        method: '[clientIncoming][response]',
        from: _user_id,
        message: data,
      });

      const { msg_ret_info = {}, msg_handle_incoming_msq_rsp: rsp } = data;
      const { uint32_ret_code, str_error_msg } = msg_ret_info;

      let reception = {};
      if (uint32_ret_code >= 0) {
        // code 非负时表示进线成功
        _report({
          module: 'c-imsdk',
          method: '[clientIncoming][response][success]',
          from: _user_id,
          message: {
            code: uint32_ret_code,
            msginfo: msg_ret_info, 
          },
        });

        if (uint32_ret_code === 19) {
          // 未接入池
          reception = {
            inWaitPool: true,
          };
        } else if (rsp) {
          // 正常接待
          _log('[clientIncoming]', rsp);
          const {
            bool_is_robot: isRobot,
            uint64_robot_id: robotId,
            uint64_kfextuin: extuin,
          } = rsp;
          if (isRobot) {
            // 当前机器人接待
            reception = {
              isRobot: true,
              robotId,
            };
          } else {
            // 人工接待
            reception = {
              extuin,
            };
          }
        }

        this.updateReception!(reception);

        // 修改会话进线状态
        this.sessionStarted = true;
        // 会话开始事件
        emitter.emit(EVENT.SESSION_START);
      } else {
        // 发生其他错误
        emitter.emit(EVENT.LOCAL_NOTIFY, {
          type: 'session_start_error',
          content: str_error_msg || `会话失败 (code:${uint32_ret_code})`,
        });

        _report({
          module: 'c-imsdk',
          method: '[clientIncoming][response][error]',
          from: _user_id,
          message: {
            code: uint32_ret_code,
          },
        });
      }
    });
  };

  // Saas 版进线处理
  sdk.c2bSessionStart = function (params: any) {
    const message = proto.createC2bSessionStartReq(params);

    // 超时处理
    const timer = setTimeout(() => {
      emitter.emit(EVENT.LOCAL_NOTIFY, {
        type: 'session_start_timeout',
        content: '会话接入超时',
      });
    }, 60 * 1000);

    _report({
      module: 'c-imsdk',
      method: '[c2bSessionStart][request]',
      from: _user_id,
      message: params,
    });

    this.sendCmdMessage!(125603, message).then((data: any) => {
      // 清理超时处理
      clearTimeout(timer);

      _report({
        module: 'c-imsdk',
        method: '[c2bSessionStart][response]',
        from: _user_id,
        message: data,
      });

      const { msg_c2b_session_start_rsp: sessionData = {} } = data;
      const { msg_ret = {}, msg_session_data: rsp } = sessionData;
      const { uint32_ret_code, str_error_msg } = msg_ret;

      let reception = {};
      if (uint32_ret_code >= 0) {
        // code 非负时表示进线成功

        if (uint32_ret_code === 19) {
          // 未接入池
          reception = {
            inWaitPool: true,
          };
        } else if (rsp) {
          // 正常接待
          _log('[sessionStart]', rsp);
          const {
            bool_is_robot: isRobot,
            uint64_robot_id: robotId,
            uint64_kfextuin: extuin,
          } = rsp;
          if (isRobot) {
            // 当前机器人接待
            reception = {
              isRobot: true,
              robotId,
            };
          } else {
            // 人工接待
            reception = {
              extuin,
            };
          }
        }

        this.updateReception!(reception);

        // 修改会话进线状态
        this.sessionStarted = true;
        // 会话开始事件
        emitter.emit(EVENT.SESSION_START);
      } else {
        // 发生其他错误
        emitter.emit(EVENT.LOCAL_NOTIFY, {
          type: 'session_start_error',
          content: str_error_msg || `会话失败 (code:${uint32_ret_code})`,
        });
      }
    });
  };

  // qd一般事件命令消息。 （注：消息 interface 为 InnerImClickEventReq，这里取名有问题）
  sdk.sendInnerClickEvent = function (params) {
    const message = proto.createMsgQdmenuReq(params);
    _report({
      module: 'c-imsdk',
      method: '[sendInnerClickEvent][request]',
      from: _user_id,
      message: params,
    });

    return this.sendCmdMessage!(303155, message).then((data: any) => {
      _report({
        module: 'c-imsdk',
        method: '[sendInnerClickEvent][response]',
        from: _user_id,
        message: data,
      });

      _log('[sendInnerClickEvent]', data);

      return data;
    });
  };

  // 结束会话
  sdk.closeSession = function (params: any) {
    const message = proto.createMsgCloseSessiongReq(params);

    _report({
      module: 'c-imsdk',
      method: '[closeSession][request]',
      from: _user_id,
      message: params,
    });
    this.sendCmdMessage!(201017, message).then((data: any) => {
      _report({
        module: 'c-imsdk',
        method: '[closeSession][response]',
        from: _user_id,
        message: data,
      });

      if (data.msg_customer_closeSession_req) {
        _log('[closeSession]', data.msg_customer_closeSession_req);
        const rsp = data.msg_customer_closeSession_req;
        if (rsp.msg_ret && !rsp.msg_ret.uint32_ret_code) {
          // 不需要C侧自己处理灰条了
          // // C侧上屏灰条消息
          // emitter.emit(EVENT.LOCAL_NOTIFY, { type: 'chat_close_session_succ', content: '' });
          this.updateReception!({});
        }
      }

      return data;
    });
  };

  // 拉取工号信息
  sdk.fetchExtuinInfo = function (params: any, isRobot: boolean) {
    _report({
      module: 'c-imsdk',
      method: '[fetchExtuinInfo][request]',
      from: _user_id,
      message: params,
    });
    if (isRobot) {
      return new Promise((resolve, reject) => {
        const data = {
          robot_id: params.extuin,
          kfuin: +params.kfuin
        }
        this.sendCmdMessage!(3, data, 1536)
          .then((data: any) => {
            const res = JSON.parse(data);
            _report({
              module: 'c-imsdk',
              method: '[fetchExtuinInfo][response]',
              from: _user_id,
              message: data,
            });
            if (res.code === 0) {
              _log(
                '[fetchExtuinInfo-rsp]',
                res.data
              );
              const { str_robot_name: name, str_robot_icon: avatar } = res.data || {};
              resolve({
                name,
                avatar,
              });
            }
            resolve(null);
          })
          .catch(() => {
            resolve(null);
          });
      });
    } else {
      return new Promise((resolve, reject) => {
        const message = proto.createMsgGetCustomerDetailReq(params);
        this.sendCmdMessage!(303002, message)
          .then((data: any) => {
            _report({
              module: 'c-imsdk',
              method: '[fetchExtuinInfo][response]',
              from: _user_id,
              message: data,
            });
            if (
              data.msg_inner_get_customer_detail_data_rsp &&
              data.msg_inner_get_customer_detail_data_rsp.msg_customer_data_rsp &&
              data.msg_inner_get_customer_detail_data_rsp.msg_customer_data_rsp
                .msg_staff_data
            ) {
              _log(
                '[fetchExtuinInfo-rsp]',
                data.msg_inner_get_customer_detail_data_rsp.msg_customer_data_rsp
                  .msg_staff_data
              );
              const { string_external_name: name, string_external_face: avatar, string_user_name: innerName } =
                data.msg_inner_get_customer_detail_data_rsp.msg_customer_data_rsp
                  .msg_staff_data;
  
              resolve({
                name,
                avatar,
                innerName,
              });
            }
            resolve(null);
          })
          .catch(() => {
            resolve(null);
          });
      });
    }
    
  };

  // 发送一些命令的通用接口
  sdk.sendCmdMessage = function (command, message, msgtype = 1064) {
    _report({
      module: 'c-imsdk',
      method: 'sendCmdMessage',
      from: _user_id,
      message: {
        command,
        msgtype,
        message,
      },
    });

    return _socketio.send(
      {
        uint32_sub_command: command, // 命令值
        uint32_sub_msgtype: msgtype, // 固定值
      },
      message
    );
  };

  sdk.on = function (type, handler) {
    if (_sdk_is_ready && type == EVENT.SDK_READY) {
      handler && handler();
      return;
    }
    emitter.on(type, handler);
  };

  sdk.off = function (type, handler) {
    emitter.off(type, handler);
  };

  sdk.emit = function (type, event) {
    emitter.emit(type, event);
  };

  sdk.setLogReport = function (report: ReportFunc) {
    _report = report;
  };

  // 设置消息中附加的tags
  sdk.setMessageTags = function (msg, tags) {
    const { uint64_msg_roaming_seq: roamingId, msg_type_info } = msg.msgHead;
    const from = msg_type_info.msg_c2c_info.uint64_from_aid;
    const to = msg_type_info.msg_c2c_info.uint64_to_aid;

    // 转化数据
    const covTags = tags.map((item) => {
      return {
        str_tag: item.tag,
        bytes_value: item.value,
      };
    });

    const params = {
      from,
      to,
      roamingId,
      tags: covTags,
    };

    const message = proto.createUpdateOnlineReq(params);
    _report({
      module: 'c-imsdk',
      method: '[setMessageTags][request]',
      from: _user_id,
      message: params,
    });

    this.sendCmdMessage!(324035, message, 1297).then((data: any) => {
      _log('[setMessageTags]', data);
      _report({
        module: 'c-imsdk',
        method: '[setMessageTags][response]',
        from: _user_id,
        message: data,
      });

      return data;
    });
  };

  /**
   * 从消息中取出C侧存储的tags
   * @param msg
   */
  sdk.getMessageTags = function (msg) {
    const { msg_tags: msgTags = [] } = msg.msgBody || {};
    const recovTags: Array<MsgTag> = msgTags.map((item) => {
      return {
        // 容错，保存的时候key名为str_tag，输出却是name，以防万一。。。
        tag: item.str_tag || item.name,
        value: item.bytes_value,
      };
    });
    return recovTags;
  };

  /**
   * 同步客户信息到企点
   * @param cInfo 客户信息
   */
  sdk.setCustomerInfo = function (cInfo: CustomerInfo) {
    const data = {};
    // 处理数据
    Object.keys(cInfo).forEach((item) => {
      if (item === 'gender') {
        data['uint32_' + item] = cInfo[item];
      } else {
        data['bytes_' + item] = cInfo[item];
      }
    });

    const message = proto.createSetAidInfoReq(data);

    _report({
      module: 'c-imsdk',
      method: '[setCustomerInfo][request]',
      from: _user_id,
      message: cInfo,
    });

    return new Promise((resolve, reject) => {
      this.sendCmdMessage!(324028, message)
        .then((data: any) => {
          _report({
            module: 'c-imsdk',
            method: '[setCustomerInfo][response]',
            from: _user_id,
            message: data,
          });

          if (data.msg_ret_info) {
            const { uint32_ret_code: code, str_error_msg: message } =
              data.msg_ret_info;
            if (code) {
              reject({
                code,
                message,
              });
            } else {
              resolve(true);
            }
          } else {
            reject({
              code: -1,
              message: '操作失败，请重试',
            });
          }
        })
        .catch(() => {
          reject({
            code: -1,
            message: '操作失败，请重试',
          });
        });
    });
  };

  /**
   * C侧主动触发满意度
   * @param params
   * @returns
   */
  sdk.activeFeedback = function (params) {
    const message = proto.createActiveFeedbackReq(params);

    _report({
      module: 'c-imsdk',
      method: '[activeFeedback][request]',
      from: _user_id,
      message: params,
    });

    return new Promise((resolve, reject) => {
      this.sendCmdMessage!(215100, message)
        .then((data: any) => {
          _report({
            module: 'c-imsdk',
            method: '[activeFeedback][response]',
            from: _user_id,
            message: data,
          });

          if (data.msg_push_satisfaction_survey_all_channel_rsp) {
            const rsp = data.msg_push_satisfaction_survey_all_channel_rsp;
            const { msg_ret = {} } = rsp;
            const { uint32_ret_code: code, str_error_msg: message } = msg_ret;
            if (code) {
              reject({
                code,
                message,
              });
            } else {
              resolve(true);
            }
          } else {
            reject({
              code: -1,
              message: '操作失败，请重试',
            });
          }
        })
        .catch((err) => {
          reject({
            code: -1,
            message: '操作失败，请重试',
          });
        });
    });
  };

  /**
   * 获取文件地址
   * @param {Object} params
   * @param {number} params.path - 文件路径
   * @param {number} params.compressed - 压缩类型：0 不压缩， 1 gzip；
   * @param {number} params.resStore - 文件存储类型，决定使用哪个cos
   *
   * @param {string} type - 文本类型， xml 或 text
   */
  sdk.createCompressedLongTextDownloadMessage = function (params: any, type: string) {
    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createCompressedLongTextDownloadMessage][${params.path}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createCompressedLongTextDownloadMessage',
      from: _user_id,
      message: params,
    });

    // 带入必要参数
    Object.assign(params, {
      kfuin: _socketio.config.kfuin, // 主号
      appid: _socketio.config.appid, // appid
    });
    return proto.createCompressedLongTextMsgDownloadReq(params, type);
  };

  /**
   * 返回退出排队信令需要是消息内容
   * @param params 
   * @param {number} params.cid - C侧用户的aid
   */
  sdk.createExitQueueMessage = function (params: any) { 
    Mango({
      report_content: {
        common_head: {
          uint64_kfuin: _socketio.config.kfuin, // 主号
          uint64_aid: _socketio.config.aid, // 工号
          uint64_client_time: new Date().getTime(),
          uint32_client_type: _socketio.config.client_type,
          uint32_appid: _socketio.config.appid,
          str_client_version: '1.0.0',
        },
        log_event: [
          {
            log_level: 4, //default:5  FATAL:1,ERR:2,WARIN:3,INFO:4,DEBUG:5
            str_log: `[c-imsdk_createExitQueueMessage][${JSON.stringify(params)}]`,
          },
        ],
      },
    });

    _report({
      module: 'c-imsdk',
      method: 'createExitQueueMessage',
      from: _user_id,
      message: params,
    });

    return proto.createExitQueueMessage(params);
  };

  /**
   * 拉取漫游消息请求
   * @param {Object} params
   * @param {number} params.to - 消息接收方
   * @param {number} params.end - 消息起始end
   * @param {Object} params.payload - 消息体
   * @param {string} params.payload.user_id - 通路id
   * @param {string} params.payload.user_type - 通路类型
   * @param {number} params.payload.pubacc_id - 公众号id
   */
  sdk.getOldMessageList = function (params: any) {
    const to = Number(params.to);
    let end = Number(params.end);
    const payload = params.payload;
    if (isNaN(to) || isNaN(end)) {
      _error('消息接收方(to)或者起始end不是数值类型', params.to, params.end);
      return;
    }

    _report({
      module: 'c-imsdk',
      method: '[getOldMessageList][request]',
      from: _user_id,
      message: {
        to,
        end,
      },
    });

    if (end < 0) {
      // 第一次拉取。需传入 us 。
      end = Date.now() * 1000;
    }

    return new Promise((resolve, reject) => {
      let message = proto.createOldMsgRoamingReq(
        to,
        end,
        this.config.roamingMsgSize
      );
      _socketio
        .send(
          {
            uint32_sub_command: 325004,
            uint32_sub_msgtype: 1064,
          },
          message
        )
        .then((data: any) => {
          let result: any = [];
          if (data['webim_roaming_adapted_rsp']) {
            result = proto.unpackMsgRoamingRsp(data);
            if (result.length > 0) {
              // 转为us.
              end = result[result.length - 1].time * 1000 + 1; // !!!注意:这里要对time加1处理，不然的话会有重复消息
            }
          }

          resolve({
            messageList: result,
            nextReqMessageTime: end,
            isCompleted:
              result.length < this.config.roamingMsgSize! ? true : false,
            isOldRoamingMsg:true,
          });

          _log('漫游消息回包:', data);
          let _log_data: any = [];
          if (result.length > 0) {
            let _maxRoam = result[result.length - 1];
            let _minRoam = result[0];
            _log_data.push({
              int64_min_roaming_id: _minRoam.seq,
              int64_max_roaming_id: _maxRoam.seq,
              int64_from_id: _maxRoam.from,
              int64_to_id: _maxRoam.to,
              int64_msg_send_time: _maxRoam.time * 1000,
              int64_user_login_time: data.login_time,
              int64_msg_recv_server_time: data.timestamp,
              uint64_gseq: data.gseq,
            });
          }

          _report({
            module: 'c-imsdk',
            method: '[getOldMessageList][response]',
            from: _user_id,
            to: to,
            message: {
              to,
              end,
              msginfo: _log_data,
            },
          });
        })
        .catch((err: any) => {
          _error('漫游消息超时:', err);

          _report({
            module: 'c-imsdk',
            method: '[getOldMessageList][tiemout]',
            from: _user_id,
            message: err,
          });

          throw {
            reason: err.result,
            seq: err.seq,
          };
        });
    });
  };

  /** 获取接待状态 */
  sdk.getReceptionStatus = function () {
    _report({
      module: 'c-imsdk',
      method: '[getReceptionStatus][request]',
      from: _user_id,
    });

    const message = proto.createMsgSessionGetReq({
      cid: _socketio.config.aid,
    });
    return this.sendCmdMessage!(201701, message).then((data) => {
      try {
        const receptionData = data.msg_session_get_rsp.msg_data;
        _report({
          module: 'c-imsdk',
          method: '[getReceptionStatus][response]',
          from: _user_id,
          message: receptionData,
        });
  
        _log('[getReceptionStatus]', receptionData);

        // 因为 无法区分出 【导航， 留言， 排队】 这些状态，
        // 目前 仅补偿 当前为 【机器人、人工接待】  时的状态
        let reception = {};
        const {
          bool_is_robot: isRobot,
          uint64_robot_id: robotId,
          msg_kfext_info,
          uint32_status: isRecepting,
        } = receptionData;

        // 人工/机器人正在接待中.
        if (isRecepting) {
          if (isRobot) {
            reception = {
              isRobot: true,
              robotId,
            };
          } else if (msg_kfext_info) {
            const { uint64_kfextuin: extuin } = msg_kfext_info;
            if(extuin){
              reception = {
                extuin,
              };
            }
          }
        }

        // 仅当检测到有效的接待状态时，才出发状态变更，否则不处理。
        if (Object.keys(reception).length > 0) {
          this.updateReception!(reception);
        }
      } catch (ex: any) {
        _error(ex);
      }
    });
  }

  /** @internal 更新接待配置 */
  sdk.updateReception = function (newReception) {
    // 当数据更新时，才抛出事件
    if (JSON.stringify(_reception) !== JSON.stringify(newReception)) {
      _reception = newReception;
      _log('[updateReception]', '11111111', _reception);
      emitter.emit(EVENT.RECEPTION_CHANGE, _reception);
    } else {
      _log('[updateReception]', '00000000', _reception);
    }
  }

  /**
   * 获取和sdk绑定的socketio
   */
  sdk.getSocket = function(){
    return _socketio;
  }

  /**
   * 创建【设置消息已被阅读】的消息包
   */
  sdk.createReportMsgReadReportReq = function (params: any): void {
    console.log('[createReportMsgReadReportReq]', params);
    const message = proto.createReportMsgReadReportReq(params);
    return message;
  }

  /**
   * 创建【拉取消息已读状态】的消息包
   * @param params 
   */
  sdk.createGetMsgReadInfoReq = function (params: any) {
    console.log('[createGetMsgReadInfoReq]', params);
    const message = proto.createGetMsgReadInfoReq(params);
    return message;
  }
  

  /**
   * 推送消息已读回执状态
   * @param params 
   * @returns 
   */
  sdk.pushMsgReadReport = function (params: any): Promise<object> {
    _report({
      module: 'c-imsdk',
      method: '[pushMsgReadReport][request]',
      from: _user_id,
      message: params,
    });

    console.log('[pushMsgReadReport]', params);
    const CMD = 370010;
    const message = proto.createReportMsgReadReportReq(params);
    return this.sendCmdMessage!(CMD, message).then((data: any) => {
      _report({
        module: 'c-imsdk',
        method: '[pushMsgReadReport][response]',
        from: _user_id,
        message: data,
      });

      return data;
    });
  }

  /**
   * 读取消息的【crm】信息
   * @param message 
   * @returns 
   */
  sdk.getMessageCrmData = function (message: MessagePackage): object {
    try {
      const crm = message
        .msg_send_req
        .msg_send_msg
        .msg_msg_head
        .msg_third_app_info
        .bytes_third_app_buf
        .bytes_crm_buf;

      return crm;
    } catch (ex: any) {
      _error('[getMessageCrmData]', ex);
    }
    return {}
  }

  /**
   * 从CRM信息中读取消息的【已读回执】信息，返回处理后的消息数据
   */
  sdk.setMessageCrmData = function (message: MessagePackage, crmData: object) {
    const crm = this.getMessageCrmData!(message);
    try {
      message
        .msg_send_req
        .msg_send_msg
        .msg_msg_head
        .msg_third_app_info
        .bytes_third_app_buf
        .bytes_crm_buf
        = { ...crm, ...crmData };

      return message;
    } catch (ex: any) {
      _error('[setMessageCrmData]', ex);
    }
    return message;
  }

  /**
   * 获得socket连接的配置信息，用于为SDKUtils发送http请求设置headers
   * @returns 
   */
  sdk.getConnectConfig = function (): any {
    return (_socketio && _socketio.config) ? _socketio.config : {};
  }

  return sdk;
}
//------------------------------------------------- 分隔线 -------------------------------------------------

/**
 * 之前SDK未考虑多实例共存的情况，全局共享唯一的单例。
 * 为了兼容老的单例模式，这里生成一个全局的单例。
 * 在 IMSDK.create 方法中返回
 */
const oneSdk = createSDKInstance();
/**
 * IMSDK 是企点IM的命名空间，提供了创建SDK实例的create方法，以及事件常量 EVENT, 消息类型常量 TYPES 和 文件对象类型常量 OBJTYPES。
 * 使用时，应先调用
 *  const imsdk = IMSDK.create(options);
 * 创建/获取 imsdk 实例，然后进行消息的操作
 */
export const IMSDK = {
  /**
   * 返回全局唯一的SDK实例
   * @param options 创建IM的选项，主要是传入 Instant 的实例
   * @param config sdk实例的配置信息
   * @returns SDK的实例
   * @example
   * ```ts
   * const imsdk = IMSDK.create();
   * ```
   */
  create: function (options?: any, config?: SDKConfig): SDK {
    if (options) {
      oneSdk.create!(options);
    }
    Object.assign(oneSdk.config, config);
    return oneSdk;
  },

  /**
   * 创建SDK实例，用于多实例共存模式。每次执行都会返回一个新的实例。
   * @param options 
   * @param config 
   * @returns SDK的实例
   * @example
   * ```ts
   * const imsdk = IMSDK.new();
   * ```
   */
  new: function (options?: any, config?: SDKConfig): SDK {
    const sdk = createSDKInstance();
    if (options) {
      sdk.create!(options);
    }
    Object.assign(sdk.config, config);
    return sdk;
  },
  /** 
   * 事件常量
   * @example
   * ```ts
   * // 当收到新消息
   * imsdk.on(IMSDK.EVENT.MESSAGE_RECEIVED, onNewMessage);
   * ```
   */
  EVENT: EVENT,
  /** 
   * 类型常量
   * @example
   * ```ts
   * if(message.type === IMSDK.TYPES.MSG_TEXT) {
   *  // 文本消息
   * }
   * ```
   */
  TYPES: TYPES,
  /** 文件对象类型常量 */
  OBJTYPES: OBJTYPES,
};
